import { useMemo } from "react"
import { useSelector } from "react-redux"
import fp from "lodash/fp"

import type {
  DeviceAlertSetting,
  PowerlineAlertSetting,
  TowerAlertSetting,
} from "types/alerts.types"

import { useOrgAlertSettingsQuery } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import {
  formatResOrgAlertSettings,
  formatSettingsById,
} from "helpers/formatters/alertSettingsFormatters"
import { ALERT_SETTINGS_TIME } from "helpers/utils/constants"

const useAlertSettings = () => {
  const orgId = useSelector(selectCurrentOrgId)
  const {
    currentData: resOrgAlertSettings,
    isLoading,
    isError,
  } = useOrgAlertSettingsQuery(
    {
      org_id: orgId as number,
    },
    {
      pollingInterval: ALERT_SETTINGS_TIME,
      skip: !orgId,
    },
  )

  const orgAlertSettings = useMemo(
    () => formatResOrgAlertSettings(resOrgAlertSettings),
    [resOrgAlertSettings],
  )

  const orgAlertsBySettingId = useMemo(
    () => formatSettingsById(orgAlertSettings),
    [orgAlertSettings],
  )

  // Disabled settings included, for the entity pages that list them.
  const orgDevicesAlertSettingsById = useMemo(
    () =>
      fp.indexBy(
        "id",
        (orgAlertSettings || []).filter((setting) => setting.entity === "device"),
      ) as { [key: string]: DeviceAlertSetting },
    [orgAlertSettings],
  )
  const orgPowerlinesAlertSettingsById = useMemo(
    () =>
      fp.indexBy(
        "id",
        (orgAlertSettings || []).filter((setting) => setting.entity === "powerline"),
      ) as { [key: string]: PowerlineAlertSetting },
    [orgAlertSettings],
  )
  const orgTowersAlertSettingsById = useMemo(
    () =>
      fp.indexBy(
        "id",
        (orgAlertSettings || []).filter((setting) => setting.entity === "tower"),
      ) as { [key: string]: TowerAlertSetting },
    [orgAlertSettings],
  )

  const orgActiveDevicesAlertSettings = useMemo(
    () =>
      (orgAlertSettings || []).filter(
        (setting) => setting.entity === "device" && !!setting.config?.active,
      ) as DeviceAlertSetting[],
    [orgAlertSettings],
  )

  const orgActiveDevicesAlertSettingsById = useMemo(
    () => fp.indexBy("id", orgActiveDevicesAlertSettings),
    [orgActiveDevicesAlertSettings],
  )

  const orgActivePowerlinesAlertSettings = useMemo(
    () =>
      (orgAlertSettings || []).filter(
        (setting) => setting.entity === "powerline" && !!setting.config?.active,
      ) as PowerlineAlertSetting[],
    [orgAlertSettings],
  )

  const orgActivePowerlinesAlertSettingsById = useMemo(
    () => fp.indexBy("id", orgActivePowerlinesAlertSettings),
    [orgActivePowerlinesAlertSettings],
  )

  const orgActiveTowersAlertSettings = useMemo(
    () =>
      (orgAlertSettings || []).filter(
        (setting) => setting.entity === "tower" && !!setting.config?.active,
      ) as TowerAlertSetting[],
    [orgAlertSettings],
  )

  const orgActiveTowersAlertSettingsById = useMemo(
    () => fp.indexBy("id", orgActiveTowersAlertSettings),
    [orgActiveTowersAlertSettings],
  )

  // Excludes group alert settings
  const orgActiveAlertSettings = useMemo(
    () => [
      ...orgActiveDevicesAlertSettings,
      ...orgActivePowerlinesAlertSettings,
      ...orgActiveTowersAlertSettings,
    ],
    [
      orgActivePowerlinesAlertSettings,
      orgActiveDevicesAlertSettings,
      orgActiveTowersAlertSettings,
    ],
  )

  const orgActiveAlertSettingsById = useMemo(
    () => fp.indexBy("id", orgActiveAlertSettings),
    [orgActiveAlertSettings],
  )

  return {
    orgAlertSettings,
    orgActiveAlertSettings,
    orgActiveAlertSettingsById,
    orgActiveDevicesAlertSettings,
    orgActiveDevicesAlertSettingsById,
    orgActivePowerlinesAlertSettings,
    orgActivePowerlinesAlertSettingsById,
    orgActiveTowersAlertSettings,
    orgActiveTowersAlertSettingsById,
    orgAlertsBySettingId,
    orgDevicesAlertSettingsById,
    orgPowerlinesAlertSettingsById,
    orgTowersAlertSettingsById,
    isLoading,
    isError,
  }
}

export default useAlertSettings
